/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { AnalyticsContext, AnalyticsListener, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import CollapseVerticalIcon from '@atlaskit/icon/core/collapse-vertical';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import { DatasourceTableView, JIRA_LIST_OF_LINKS_DATASOURCE_ID } from '@atlaskit/link-datasource';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AnimatedWrapper } from '@atlassian/ai-common';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
	onAIAnswerDialogClick,
	onJiraNlToJqlCardLoaded,
	SmartLinksAnalyticsContextLocation,
} from '../../../../ai-answer-dialog/analytics';
import type { NonGenerativeAIAnswerResultShownProps } from '../../../../ai-answer-dialog/types';
import { SainAction, useSAINUsageCriteriaMet } from '../../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../../common/controller/analytics/use-analytics';
import { logger } from '../../../../common/utils/logger';
import { type AiBrand, AnswerCardType } from '../../../../constants';
import { sha256Hash } from '../../../../utils';
import { AIDisclaimerAndFeedback } from '../../../common/ui/ai-disclaimer-and-feedback';
import { SmartAnswerCardBodyGenerating } from '../../../common/ui/smart-answer-card-body-generating';
import { useIssueSearchQuery } from '../../api/useIssueSearchQuery/useIssueSearchQuery';
import { useNaturalLanguageToJqlQuery } from '../../api/useNaturalLanguageToJqlQuery/useNaturalLanguageToJqlQuery';

import { ErrorCard } from './ErrorCard';
import { messages } from './messages';
import { ViewAllButton } from './ViewAllButton';

const styles = cssMap({
	container: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.sunken'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingInline: token('space.200'),
	},
	tableContainer: {
		position: 'relative',
		borderRadius: token('radius.large'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		overflow: 'hidden',
	},
	viewAllContainer: {
		textAlign: 'left',
	},
});

const TABLE_HEIGHT_COLLAPSED = 240;
const TABLE_HEIGHT_EXPANDED = 495;

const buildJiraLink = ({ issueUrl, jql }: { issueUrl: string; jql: string }): string | null => {
	if (!issueUrl) {
		return null;
	}
	try {
		const hostname = new URL(issueUrl).hostname;
		const jiraLink = new URL(`https://${hostname}/issues/`);
		jiraLink.searchParams.append('jql', jql);
		return jiraLink.toString();
	} catch {
		return null;
	}
};

export const JiraNlToJqlSmartlinkCard = memo(
	(props: {
		query: string;
		cloudId: string;
		aiBrand: AiBrand;
		searchSessionId: string;
		workspaceId: string;
		product: string;
		onFMPReached: () => void;
	}) => {
		const renderStartTime = useRef(performance.now());
		const analyticsFiredRef = useRef(false);
		const { query, cloudId, aiBrand, searchSessionId, workspaceId, product, onFMPReached } =
			props;
		const [issueUrl, setIssueUrl] = useState<string | null>(null);
		const { formatMessage } = useIntl();
		const [fireAnalyticsEvent] = useAnalytics();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const { trackAIInteractionInit } = useAIEventsInstrumentation();
		const { commonAttributes, setData } = useAIAnswerContext();

		const [isHidden, setIsHidden] = useState(false);
		const [isExpanded, setIsExpanded] = useState(false);

		const {
			loading: jqlLoading,
			error: jqlError,
			jql,
		} = useNaturalLanguageToJqlQuery({ cloudId, query, workspaceId, product });
		const {
			issues,
			totalIssuesCount,
			loading: issuesLoading,
			error: issuesError,
			extensions: issuesExtensions,
		} = useIssueSearchQuery({ cloudId, jql, workspaceId, product });

		const analyticsAttributes = useMemo<NonGenerativeAIAnswerResultShownProps>(
			() => ({
				searchSessionId,
				queryHash: sha256Hash(query),
				brand: aiBrand,
				source: 'jira_nl_2_jql_card',
				query,
				answerString: jql ? sha256Hash(jql) : '',
				answerCardType: AnswerCardType.JIRA_NATURAL_LANGUAGE_QUERY,
			}),
			[searchSessionId, query, aiBrand, jql],
		);

		const analyticsAttributesWithoutUGC = useMemo<Omit<NonGenerativeAIAnswerResultShownProps, 'query' | 'answerString'>>(
			() => ({
				searchSessionId,
				queryHash: sha256Hash(query),
				brand: aiBrand,
				source: 'jira_nl_2_jql_card',
				answerCardType: AnswerCardType.JIRA_NATURAL_LANGUAGE_QUERY,
			}),
			[searchSessionId, query, aiBrand],
		);

		const fireJiraNlToJqlCardLoaded = useCallback(
			({
				success,
				endState,
			}: {
				success: boolean;
				endState?: 'answered' | 'no-issues' | 'jql-error' | 'issues-error';
			}) => {
				const apiLoadedTime = performance.now();
				const totalDuration = apiLoadedTime - renderStartTime.current;
				fireAnalyticsEvent(
					onJiraNlToJqlCardLoaded({
						...analyticsAttributes,
						success,
						loadTime: totalDuration,
						endState,
					}),
				);
			},
			[fireAnalyticsEvent, analyticsAttributes],
		);

		// Using the first issue's webUrl to build a Jira URL for the "View all" button
		useEffect(() => {
			if (!issuesLoading && !issuesError && issues?.length && issues[0]) {
				try {
					const firstIssueUrl = issues[0].node.webUrl;
					setIssueUrl(buildJiraLink({ issueUrl: firstIssueUrl || '', jql: jql || '' }));
				} catch (error) {
					logger.error({ error, functionName: 'JiraNlToJqlCard' });
				}
			}
		}, [issuesLoading, issuesError, issues, jql]);

		useEffect(
			() => {
				trackAIInteractionInit();
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[],
		);

		// Set debug data for online debugger when API response is available
		// Separate from analytics effect to avoid infinite loop with commonAttributes
		useEffect(() => {
			if (!issuesLoading && issuesExtensions?.gateway && fg('kd_sain_online_debugger')) {
				setData({
					errorState: null,
					format: null,
					uniqueSources: [],
					disclaimer: null,
					extraAPIAnalyticsAttributes: {
						traceId: issuesExtensions.gateway.trace_id ?? null,
						requestId: issuesExtensions.gateway.request_id ?? null,
					},
				});
			}
		}, [issuesLoading, issuesExtensions?.gateway, setData]);

		// fire Analytics Events on final state changes
		useEffect(() => {
			if (analyticsFiredRef.current) {
				return;
			}

			if (
				!jqlLoading &&
				!jqlError &&
				!issuesLoading &&
				!issuesError &&
				issues?.length &&
				issues[0]
			) {
				/*
				 * * Success block
				 */
				analyticsFiredRef.current = true;
				onFMPReached();
				fireJiraNlToJqlCardLoaded({ success: true, endState: 'answered' });
				if (fg('kd_fix_nl2jql_mau_instrumentation')) {
					fireSAINUsageEvent({
						commonAttributes: {
							...commonAttributes,
							extraAttributes: {
								...commonAttributes?.extraAttributes,
								requestId: issuesExtensions?.gateway?.request_id ?? null,
							},
						}
					});
				} else {
					fireSAINUsageEvent({ commonAttributes });
				}
			} else if (!jqlLoading && jqlError) {
				/*
				 * * JQL error block
				 */
				analyticsFiredRef.current = true;
				fireJiraNlToJqlCardLoaded({ success: false, endState: 'jql-error' });
			} else if (!issuesLoading && issuesError) {
				/*
				 * * Issues error block
				 */
				analyticsFiredRef.current = true;
				fireJiraNlToJqlCardLoaded({ success: false, endState: 'issues-error' });
			} else if (!jqlLoading && !issuesLoading && issues?.length === 0) {
				/*
				 * No issues block: not an error, but we mark as failure because the user sees an error card.
				 */
				analyticsFiredRef.current = true;
				fireJiraNlToJqlCardLoaded({ success: false, endState: 'no-issues' });
			}
		}, [
			jqlLoading,
			issuesLoading,
			issuesError,
			jqlError,
			issues,
			onFMPReached,
			fireJiraNlToJqlCardLoaded,
			fireSAINUsageEvent,
			commonAttributes,
			issuesExtensions?.gateway?.request_id,
		]);

		const fireEngagementEvent = useCallback((sainAction: SainAction) => {
			fireSAINUsageEvent({
				metCriteria: sainAction,
				commonAttributes: {
					...commonAttributes,
					extraAttributes: {
						...commonAttributes?.extraAttributes,
						requestId: issuesExtensions?.gateway?.request_id ?? null,
					},
				}
			});
		}, [fireSAINUsageEvent, commonAttributes, issuesExtensions?.gateway?.request_id]);

		const handleDatasourceAnalyticsEvent = useCallback((event: UIAnalyticsEvent) => {
			const { payload } = event;

			if (payload.action === 'clicked') {
				if (fg('kd_fix_nl2jql_mau_instrumentation')) {
					if (payload.actionSubject === 'link') {
						fireEngagementEvent(SainAction.JIRA_NL_TO_JQL_WORK_ITEM_CLICKED);
						fireAnalyticsEvent(onAIAnswerDialogClick({ ...commonAttributes, elementInteracted: 'jira-issue-key', answerString: jql ? sha256Hash(jql) : '' }));
					} else {
						fireEngagementEvent(SainAction.JIRA_NL_TO_JQL_CLICKED);
					}
				}
				fireAnalyticsEvent(onAIAnswerDialogClick({
					...commonAttributes,
					elementInteracted: payload.actionSubject
				}));
			}
		}, [fireAnalyticsEvent, commonAttributes, fireEngagementEvent, jql]);

		const onViewAllButtonClicked = useCallback(() => {
			fireAnalyticsEvent(
				onAIAnswerDialogClick({
					...commonAttributes,
					elementInteracted: 'jira-view-all-button',
					answerString: jql ? sha256Hash(jql) : '',
				}),
			);
			if (fg('kd_fix_nl2jql_mau_instrumentation')) {
				fireEngagementEvent(SainAction.JIRA_NL_TO_JQL_VIEW_ALL);
			}
		}, [fireAnalyticsEvent, fireEngagementEvent, commonAttributes, jql]);

		const onExpandButtonClicked = useCallback(() => {
			setIsExpanded(true);
			fireAnalyticsEvent(aiAnswerExpandClicked({
				...commonAttributes,
			}));
			fireEngagementEvent(SainAction.EXPAND_CLICKED);
		}, [fireAnalyticsEvent, commonAttributes, fireEngagementEvent]);

		const onCollapseButtonClicked = useCallback(() => {
			setIsExpanded(false);
			fireAnalyticsEvent(aiAnswerCollapseClicked({
				...commonAttributes,
			}));
		}, [fireAnalyticsEvent, commonAttributes]);

		const onHideButtonClicked = useCallback(() => {
			setIsHidden(true);
			fireAnalyticsEvent(onAIAnswerDialogClick({
				...commonAttributes,
				elementInteracted: 'jira-hide-table-button',
			}));
		}, [fireAnalyticsEvent, commonAttributes]);

		const onRevealButtonClicked = useCallback(() => {
			setIsHidden(false);
			fireAnalyticsEvent(onAIAnswerDialogClick({
				...commonAttributes,
				elementInteracted: 'jira-reveal-table-button',
			}));
		}, [fireAnalyticsEvent, commonAttributes]);

		if (jqlLoading || issuesLoading) {
			return (
				<AnimatedWrapper>
					<SmartAnswerCardBodyGenerating heightOverride="jiraNlToJqlCard" />
				</AnimatedWrapper>
			);
		}

		if (jqlError || issuesError || issues?.length === 0) {
			return <ErrorCard query={query} />;
		}

		const headerSection = (
			<Inline spread='space-between' alignBlock='center'>
				<Inline alignBlock='center' space='space.075'>
					<AiGenerativeTextSummaryIcon label="AI Content" size="medium" />
					<Heading size="xsmall" as="h2">{formatMessage(messages.smartlinkCardTitle)}</Heading>
				</Inline>
				<Inline space='space.100' alignBlock='center'>
					{totalIssuesCount && totalIssuesCount > 0 && issueUrl ? (
						<Box xcss={styles.viewAllContainer}>
							<ViewAllButton
								url={issueUrl}
								callback={onViewAllButtonClicked}
								spacing="compact"
							/>
						</Box>
					) : null}
					{!isHidden && issues?.length && issues?.length > 4 && <IconButton
						appearance="default"
						onClick={isExpanded ? onCollapseButtonClicked : onExpandButtonClicked}
						testId="jira-sain-card-expand-collapse-button"
						icon={isExpanded ? CollapseVerticalIcon : GrowVerticalIcon}
						spacing="compact"
						label={isExpanded ? formatMessage(messages.collapse) : formatMessage(messages.expand)}
						isTooltipDisabled={false}
					/>}
					<IconButton
						appearance="default"
						onClick={isHidden ? onRevealButtonClicked : onHideButtonClicked}
						testId={isHidden ? 'jira-sain-card-reveal-button' : 'jira-sain-card-hide-button'}
						icon={isHidden ? EyeOpenIcon : EyeOpenStrikethroughIcon}
						spacing="compact"
						label={isHidden ? formatMessage(messages.unhide) : formatMessage(messages.hide)}
						isTooltipDisabled={false}
					/>
				</Inline>
			</Inline>
		);

		if (isHidden) {
			return (
				<Stack space="space.150" xcss={styles.container} testId="jira-nl-to-jql-card-wrapper">
					{headerSection}
				</Stack>
			);
		}

		return (
			<Stack space="space.150" xcss={styles.container} testId="jira-nl-to-jql-card-wrapper">
				{headerSection}

				<Box xcss={styles.tableContainer}>
					<AnalyticsListener channel="media" onEvent={handleDatasourceAnalyticsEvent}>
						<AnalyticsContext
							data={{
								attributes: {
									location: SmartLinksAnalyticsContextLocation.SAIN_JIRA_NL_TO_JQL_CARD,
									...analyticsAttributesWithoutUGC,
								},
								location: SmartLinksAnalyticsContextLocation.SAIN_JIRA_NL_TO_JQL_CARD,
							}}
						>
							<SmartCardProvider>
								{/* reference: https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/platform/packages/linking-platform/link-datasource/examples-helpers/buildJiraIssuesTable.tsx */}
								<DatasourceTableView
									datasourceId={JIRA_LIST_OF_LINKS_DATASOURCE_ID}
									parameters={{
										cloudId: props.cloudId,
										jql
									}}
									scrollableContainerHeight={isExpanded ? TABLE_HEIGHT_EXPANDED : TABLE_HEIGHT_COLLAPSED}
								/>
							</SmartCardProvider>
						</AnalyticsContext>
					</AnalyticsListener>
				</Box>
				<Box>
					<AIDisclaimerAndFeedback showFeedbackButtons={true} showCopyButton copyText={issueUrl ?? ''} />
				</Box>
			</Stack>
		);
	},
);
